import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Typography } from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchGroups,
  fetchLightweightUsersV4,
  fetchLinkedServices,
  selectActiveLinkedService,
  selectGroupsStatus,
  selectGroupsWithDefaults,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import ConfirmationModal from '../components/modals/confirmation-modal'
import {
  clearComplianceUserData,
  fetchComplianceRequirements,
  fetchComplianceRequirementDetail,
  fetchComplianceProgress,
  selectComplianceRequirementDetail,
  selectComplianceRequirementDetailStatus,
} from '../compliance.slice'
import { updateComplianceRequirement } from '../utils/api'
import { COMPLIANCE_URLS } from '../constants'
import {
  ComplianceFormData,
  ComplianceWizardStep,
  credentialItemFromPayload,
  credentialItemToPayload,
  recertItemFromPayload,
  recertItemToPayload,
} from './types'
import ComplianceWizardNavigation from './components/form-wizard-navigation'
import ComplianceDetailsStep from './components/form-details-step'
import ComplianceRequirementsStep from './components/form-requirements-step'
import ComplianceUserSelectionStep from './components/form-user-selection-step'
import ComplianceReviewStep from './components/form-review-step'

type InnerProps = {
  id: string
  initialData: ComplianceFormData
}

function ComplianceEditForm({ id, initialData }: InnerProps) {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID || ''),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID || ''),
  )
  let groups = useAppSelector(selectGroupsWithDefaults(linkedServiceID || ''))
  let groupsStatus = useAppSelector(selectGroupsStatus)
  let linkedServiceName = useLinkedServiceName(linkedServiceID)

  let [step, setStep] = useState<ComplianceWizardStep>('details')
  let [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  let [isSubmitting, setIsSubmitting] = useState(false)
  let { showError, NotificationComponent } = useNotifications()

  let form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: ({ value }) => {
        let fieldErrors: Array<[string, string]> = [
          ...(!value.name
            ? [['name', 'Name is required'] as [string, string]]
            : []),
          // Requirement dates are being phased out; re-enable if they return.
          // ...(!value.issueDate
          //   ? [['issueDate', 'Issue date is required'] as [string, string]]
          //   : []),
          // ...(!value.expirationDate
          //   ? [
          //       ['expirationDate', 'Expiration date is required'] as [
          //         string,
          //         string,
          //       ],
          //     ]
          //   : []),
          ...(value.userIDs.length === 0
            ? [
                ['userIDs', 'At least one user must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]
        return fieldErrors.length > 0
          ? { fields: Object.fromEntries(fieldErrors) }
          : undefined
      },
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)
      await updateComplianceRequirement(id, {
        name: value.name,
        expirationDate: value.expirationDate,
        alertSetting: value.alertSetting,
        alertType: value.alertType,
        userIDs: value.userIDs,
        licenseItems: value.licenseItems,
        credentialItems: value.credentialItems.map(credentialItemToPayload),
        nremtItems: value.nremtItems,
        recertItems: value.recertItems.map(recertItemToPayload),
      })
        .then(() => {
          if (linkedServiceID && linkedServiceID !== 'unselected') {
            dispatch(fetchComplianceRequirements(linkedServiceID))
          }
          dispatch(fetchComplianceRequirementDetail(id))
          dispatch(fetchComplianceProgress(id))
          dispatch(clearComplianceUserData())
          navigate(COMPLIANCE_URLS.DETAIL(id))
        })
        .catch((err: unknown) => {
          let errorMessage =
            err instanceof Error ? err.message : 'Unknown error'
          // eslint-disable-next-line no-console
          console.error('Error updating compliance requirement:', errorMessage)
          showError('Failed to update compliance requirement')
        })
        .finally(() => setIsSubmitting(false))
    },
  })

  useEffect(() => {
    if (linkedServiceID && linkedServiceID !== 'unselected') {
      if (
        lightweightUsersStatus !== 'fulfilled' &&
        lightweightUsersStatus !== 'pending'
      ) {
        dispatch(fetchLightweightUsersV4(linkedServiceID))
      }
    }
  }, [linkedServiceID])

  useEffect(() => {
    if (groupsStatus !== 'fulfilled' && groupsStatus !== 'pending') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus])

  let handleCancel = (values?: ComplianceFormData) => {
    let isDirty =
      values &&
      (values.name !== initialData.name ||
        values.expirationDate !== initialData.expirationDate ||
        values.alertSetting !== initialData.alertSetting ||
        values.alertType !== initialData.alertType ||
        values.userIDs.length !== initialData.userIDs.length ||
        values.userIDs.some((uid) => !initialData.userIDs.includes(uid)) ||
        values.licenseItems.length !== initialData.licenseItems.length ||
        values.credentialItems.length !== initialData.credentialItems.length ||
        values.nremtItems.length !== initialData.nremtItems.length ||
        values.recertItems.length !== initialData.recertItems.length)
    if (isDirty) {
      setCancelDialogOpen(true)
    } else {
      navigate(COMPLIANCE_URLS.INDEX)
    }
  }

  return (
    <>
      <PageTitleWithLinkedService
        pageTitle="Edit Compliance Requirement"
        moduleName="Compliance"
        linkedServiceName={linkedServiceName || ''}
        handleBackClick={() => handleCancel(form.state.values)}
      />
      <form.Subscribe
        selector={(state) => ({
          values: state.values,
          fieldMeta: state.fieldMeta,
        })}
      >
        {({ values, fieldMeta }) => {
          let canProceedToUsers = Boolean(
            values.name &&
            fieldMeta.name?.isValid &&
            !fieldMeta.name?.errors?.length,
            // Requirement dates are being phased out; re-enable if they return.
            // values.issueDate &&
            // fieldMeta.issueDate?.isValid &&
            // !fieldMeta.issueDate?.errors?.length &&
            // values.expirationDate &&
            // fieldMeta.expirationDate?.isValid &&
            // !fieldMeta.expirationDate?.errors?.length,
          )

          let canProceedToReview = Boolean(
            canProceedToUsers && values.userIDs.length > 0,
          )

          return (
            <>
              <ComplianceWizardNavigation
                currentStep={step}
                onStepChange={setStep}
                canProceedToUsers={canProceedToUsers}
                canProceedToReview={canProceedToReview}
              />
              {step === 'details' && (
                <ComplianceDetailsStep
                  form={form}
                  onCancel={() => handleCancel(values)}
                  onContinue={() => setStep('requirements')}
                  canContinue={canProceedToUsers}
                />
              )}
              {step === 'requirements' && (
                <ComplianceRequirementsStep
                  licenseItems={values.licenseItems}
                  credentialItems={values.credentialItems}
                  nremtItems={values.nremtItems}
                  recertItems={values.recertItems}
                  onLicenseItemsChange={(items) =>
                    form.setFieldValue('licenseItems', items)
                  }
                  onCredentialItemsChange={(items) =>
                    form.setFieldValue('credentialItems', items)
                  }
                  onNREMTItemsChange={(items) =>
                    form.setFieldValue('nremtItems', items)
                  }
                  onRecertItemsChange={(items) =>
                    form.setFieldValue('recertItems', items)
                  }
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('details')}
                  onContinue={() => setStep('users')}
                  canContinue={canProceedToUsers}
                />
              )}
              {step === 'users' && (
                <ComplianceUserSelectionStep
                  userIDs={values.userIDs}
                  users={lightweightUsers}
                  groups={groups as any}
                  onUserIDsChange={(ids) => form.setFieldValue('userIDs', ids)}
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('requirements')}
                  onContinue={() => setStep('review')}
                  canContinue={canProceedToReview}
                  isLoadingUsers={lightweightUsersStatus === 'pending'}
                />
              )}
              {step === 'review' && (
                <ComplianceReviewStep
                  formData={values}
                  users={lightweightUsers}
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('users')}
                  onSubmit={() => form.handleSubmit()}
                  isSubmitting={isSubmitting}
                  submitLabel="Update Requirement"
                />
              )}
            </>
          )
        }}
      </form.Subscribe>
      <ConfirmationModal
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={() => navigate(COMPLIANCE_URLS.INDEX)}
        title="Discard Changes?"
        confirmText="Yes, Discard"
        cancelText="Continue Editing"
        confirmColor="error"
      >
        <Typography>Are you sure you want to discard your changes?</Typography>
      </ConfirmationModal>
      {NotificationComponent}
    </>
  )
}

export default function ComplianceEdit() {
  let { id } = useParams<{ id: string }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let detail = useAppSelector(selectComplianceRequirementDetail(id ?? ''))
  let detailStatus = useAppSelector(
    selectComplianceRequirementDetailStatus(id ?? ''),
  )

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (!id) return
    if (detailStatus !== 'fulfilled' && detailStatus !== 'pending') {
      dispatch(fetchComplianceRequirementDetail(id))
    }
  }, [id])

  let initialData: ComplianceFormData | null = (() => {
    if (!detail) return null
    let capitalize = (s: string) =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : s
    return {
      name: detail.name,
      linkedServiceID: detail.linkedServiceID,
      issueDate: detail.issueDate,
      expirationDate: detail.expirationDate,
      alertSetting: capitalize(
        detail.alertSetting,
      ) as ComplianceFormData['alertSetting'],
      alertType: capitalize(
        detail.alertType,
      ) as ComplianceFormData['alertType'],
      userIDs: detail.userIDs ?? [],
      licenseItems: (detail.licenseItems ?? []).map((item) => ({
        id: item.id,
        state: item.state,
        type: item.type,
      })),
      credentialItems: (detail.credentialItems ?? []).map(
        credentialItemFromPayload,
      ),
      nremtItems: (detail.nremtItems ?? []).map((item) => ({ id: item.id })),
      recertItems: (detail.recertItems ?? []).map(recertItemFromPayload),
    }
  })()

  if (detailStatus === 'rejected') {
    return (
      <Container maxWidth="xl" className="py-6">
        <Typography color="error">
          Failed to load compliance requirement.
        </Typography>
      </Container>
    )
  }

  if (!initialData) {
    return (
      <Container maxWidth="xl" className="py-6">
        <PageTitleWithLinkedService
          pageTitle="Edit Compliance Requirement"
          moduleName="Compliance"
          handleBackClick={() => navigate(COMPLIANCE_URLS.INDEX)}
          isLoading
        />
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <ComplianceEditForm id={id!} initialData={initialData} />
    </Container>
  )
}
